import { utils as sheetUtils, write as writeWorkbook } from 'xlsx';
import type { ExportCell, ExportColumn, ExportResponse } from '@unit/shared';

/**
 * Turns the server's export table into a file. The server owns the rows and
 * the column set; this is only the encoding.
 *
 * CSV is UTF-8 with a BOM: without it Excel on Windows opens Hebrew as
 * mojibake. Dates stay ISO in both formats — a spreadsheet will parse them,
 * and a locale-formatted string would be ambiguous when re-imported.
 */
export type ExportFormat = 'csv' | 'xlsx';

const BOM = String.fromCharCode(0xfeff);

const header = (columns: ExportColumn[], language: string): string[] =>
  columns.map((c) => (language.startsWith('en') ? c.en : c.he));

function csvCell(cell: ExportCell): string {
  if (cell === null) return '';
  const s = String(cell);
  // A quote, a separator or a line break needs quoting; a leading formula
  // character is escaped so a cell can never execute when opened in Excel.
  const escaped = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(escaped) ? `"${escaped.replace(/"/g, '""')}"` : escaped;
}

export function toCsvText(table: ExportResponse, language: string): string {
  const lines = [header(table.columns, language), ...table.rows].map((row) => row.map(csvCell).join(','));
  return `${lines.join('\r\n')}\r\n`;
}

export function toCsv(table: ExportResponse, language: string): Blob {
  return new Blob([BOM, toCsvText(table, language)], { type: 'text/csv;charset=utf-8' });
}

export function toXlsxBytes(table: ExportResponse, language: string): ArrayBuffer {
  const sheet = sheetUtils.aoa_to_sheet([header(table.columns, language), ...table.rows]);
  const workbook = sheetUtils.book_new();
  sheetUtils.book_append_sheet(workbook, sheet, table.dataset);
  if (!language.startsWith('en')) workbook.Workbook = { Views: [{ RTL: true }] };
  return writeWorkbook(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
}

export function toXlsx(table: ExportResponse, language: string): Blob {
  return new Blob([toXlsxBytes(table, language)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

export function exportFileName(table: ExportResponse, format: ExportFormat): string {
  return `${table.dataset}-${table.generatedAt.slice(0, 10)}.${format}`;
}

/** Hands the bytes to the browser as a download; same mechanism as the PDF button. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
